import type { CapabilityReport, RuntimeMode } from "@/runtime/types";

export interface CapabilityEnvironment {
  secureContext: boolean;
  xr: Pick<XRSystem, "isSessionSupported"> | undefined;
  mediaDevices: Pick<MediaDevices, "getUserMedia"> | undefined;
  coarsePointer: boolean;
  webgl?: boolean;
}

export async function detectCapabilities(
  environment: CapabilityEnvironment = browserEnvironment(),
): Promise<CapabilityReport> {
  const webxr = environment.secureContext && Boolean(
    environment.xr && await environment.xr.isSessionSupported("immersive-ar").catch(() => false),
  );
  const camera = environment.secureContext && Boolean(environment.mediaDevices?.getUserMedia);
  const webgl = environment.webgl ?? false;
  return {
    secureContext: environment.secureContext,
    webxr,
    camera,
    coarsePointer: environment.coarsePointer,
    webgl,
    preferredMode: chooseRuntimeMode({ webxr, camera, coarsePointer: environment.coarsePointer }),
  };
}

export function chooseRuntimeMode(
  capability: Pick<CapabilityReport, "webxr" | "camera" | "coarsePointer">,
): RuntimeMode {
  if (capability.webxr) return "webxr";
  // A camera preview without world tracking only makes sense on a handheld
  // device the visitor can point at something; on desktop it is just a webcam.
  if (capability.camera && capability.coarsePointer) return "camera-viewer";
  return "non-ar";
}

function browserEnvironment(): CapabilityEnvironment {
  return {
    secureContext: window.isSecureContext,
    xr: navigator.xr,
    mediaDevices: navigator.mediaDevices,
    coarsePointer: window.matchMedia("(pointer: coarse)").matches,
    webgl: supportsWebGl(),
  };
}

export function supportsWebGl(documentLike: Pick<Document, "createElement"> = document): boolean {
  try {
    const canvas = documentLike.createElement("canvas");
    const context = canvas.getContext("webgl2") || canvas.getContext("webgl");
    if (!context) return false;
    context.getExtension?.("WEBGL_lose_context")?.loseContext();
    return true;
  } catch {
    return false;
  }
}
